import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail, getUTM } from "@/lib/api";
import { track } from "@/lib/analytics";

export default function NewsletterForm({ source = "/", compact = false }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email, consent: true, source, utm: getUTM() });
      setDone(true);
      track("newsletter_submit", { source });
      toast.success(data.message || "Subscribed!");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <p className="flex items-center gap-2 text-sm font-medium text-brand-teal" data-testid="newsletter-success">
        <CheckCircle2 className="h-4 w-4" /> You're subscribed — thanks!
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="w-full" data-testid="newsletter-form">
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          aria-label="Email address"
          data-testid="newsletter-email-input"
          className="w-full rounded-full border border-border bg-background px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="newsletter-submit-btn"
          className="group inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full bg-foreground px-4 py-2.5 text-sm font-semibold text-background transition-all hover:-translate-y-0.5 disabled:opacity-60"
        >
          {loading ? "…" : "Subscribe"}
          {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
        </button>
      </div>
      {!compact && (
        <p className="mt-2 text-xs text-muted-foreground">Product updates and founder stories. No spam, unsubscribe anytime.</p>
      )}
    </form>
  );
}
